import type { Filters } from '@ipfabric/utils/table/filters/filters';
import type { RuleFormValues } from './ruleFormSchema';

export type RuleCondition = RuleFormValues['conditions'][number];

const operatorToApi = (operator: RuleCondition['operator']): string =>
  operator === 'isNot' ? 'nreg' : 'reg';

const negateOperator = (
  operator: RuleCondition['operator'],
): RuleCondition['operator'] => (operator === 'is' ? 'isNot' : 'is');

const operatorFromApi = (operator: unknown): RuleCondition['operator'] =>
  operator === 'nreg' || operator === 'nireg' ? 'isNot' : 'is';

const negateCondition = (condition: RuleCondition): Filters => ({
  [condition.property]: [
    operatorToApi(negateOperator(condition.operator)),
    condition.regex,
  ],
});

export const conditionsToFilter = (conditions: RuleCondition[]): Filters => {
  const filter: Filters = {};
  for (const condition of conditions) {
    filter[condition.property] = [
      operatorToApi(condition.operator),
      condition.regex,
    ];
  }
  return filter;
};

/**
 * Builds a filter for devices that do not match all conditions.
 * NOT(AND conditions) is expressed as OR of individually negated conditions.
 */
export const conditionsToUnmatchedFilter = (
  conditions: RuleCondition[],
): Filters => {
  if (conditions.length === 0) {
    return {};
  }
  if (conditions.length === 1) {
    const condition = conditions[0];
    if (condition) {
      return negateCondition(condition);
    }
  }
  return { or: conditions.map(negateCondition) };
};

export const filterToConditions = (
  filter: Filters | null | undefined,
): RuleCondition[] => {
  if (!filter) {
    return [];
  }

  // Production filters are flat AND objects; tolerate a single `and` nesting.
  const sources: Filters[] = Array.isArray(filter.and) ? filter.and : [filter];

  const conditions: RuleCondition[] = [];
  for (const source of sources) {
    for (const [column, params] of Object.entries(source)) {
      if (column === 'and' || column === 'or' || !Array.isArray(params)) {
        continue;
      }
      const [operator, regex] = params as [unknown, unknown];
      conditions.push({
        property: column as RuleCondition['property'],
        operator: operatorFromApi(operator),
        regex: typeof regex === 'string' ? regex : String(regex ?? ''),
      });
    }
  }
  return conditions;
};
